export class ProductAutocomplete {
  constructor(options = {}) {
    this.input =
      options.input || document.getElementById("product-search-autocomplete");
    this.resultContainer =
      options.resultContainer ||
      document.getElementById("product-search-autocomplete-result");
    this.debounceDelay = options.debounceDelay || 500;
    this.minQueryLength = options.minQueryLength || 2;
    this.isLoading = false;
    this.debounceTimer = null;
    this.currentRequest = null;

    if (!this.input) {
      console.error("ProductAutocomplete: Input element not found");
      return;
    }

    // Detect search type (inline vs full)
    this.searchType = this.detectSearchType();
    this.init();
  }

  detectSearchType() {
    // Check if we're in a full search container
    const searchContainer = this.input.closest(
      "#product-search-form-container"
    );
    if (
      searchContainer &&
      searchContainer.classList.contains("product-search-full")
    ) {
      return "full";
    }
    return "inline";
  }

  init() {
    // Create result container if it doesn't exist
    if (!this.resultContainer) {
      this.resultContainer = document.createElement("div");
      this.resultContainer.id = "product-search-autocomplete-result";
      this.resultContainer.style.display = "none";

      if (this.searchType === "full") {
        // For full search, use the dedicated container
        const fullResultsContainer = document.getElementById(
          "product-search-full-results"
        );
        if (fullResultsContainer) {
          this.resultContainer = fullResultsContainer;
        } else {
          // Fallback: insert after the search input container
          const searchContainer = this.input.closest(".flex");
          if (searchContainer) {
            searchContainer.style.position = "relative";
            searchContainer.appendChild(this.resultContainer);
          }
        }
      } else {
        // For inline search, insert after the search input container
        const searchContainer = this.input.closest(".flex");
        if (searchContainer) {
          searchContainer.style.position = "relative";
          searchContainer.appendChild(this.resultContainer);
        }
      }
    }

    this.bindEvents();
  }

  bindEvents() {
    // Input events
    this.input.addEventListener("input", (e) => {
      this.handleInput(e.target.value);
    });

    this.input.addEventListener("focus", () => {
      if (this.input.value.length >= this.minQueryLength) {
        this.showResults();
      }
    });

    this.input.addEventListener("blur", () => {
      // Only hide on blur for inline search, not for full search
      if (this.searchType === "inline") {
        // Delay hiding to allow clicking on results
        setTimeout(() => {
          this.hideResults();
        }, 200);
      }
    });

    // Keyboard navigation
    this.input.addEventListener("keydown", (e) => {
      this.handleKeydown(e);
    });

    // Click outside to close (only for inline search)
    document.addEventListener("click", (e) => {
      if (this.searchType === "inline") {
        if (
          !this.input.contains(e.target) &&
          !this.resultContainer.contains(e.target)
        ) {
          this.hideResults();
        }
      }
    });

    // Handle form submission
    const form = this.input.closest("form");
    if (form) {
      form.addEventListener("submit", (e) => {
        e.preventDefault();
        const keyword = this.input.value.trim();
        if (keyword && keyword.length >= this.minQueryLength) {
          // Redirect to clean URL with search keyword only
          const url = new URL("/product", window.location.origin);
          url.searchParams.set("q", keyword.trim());
          window.location.href = url.toString();
        }
      });
    }
  }

  handleInput(query) {
    // Clear previous timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Cancel previous request if still pending
    if (this.currentRequest) {
      this.currentRequest.abort();
    }

    // Hide results if query is too short or empty
    if (query.length < this.minQueryLength) {
      this.hideResults();
      return;
    }

    // Debounce the search
    this.debounceTimer = setTimeout(() => {
      this.performSearch(query);
    }, this.debounceDelay);
  }

  async performSearch(query) {
    if (this.isLoading) return;

    this.isLoading = true;
    this.showLoading();

    try {
      // Create AbortController for this request
      this.currentRequest = new AbortController();

      // Use different template based on search type
      const templateParam =
        this.searchType === "full"
          ? "autocomplete_full=true"
          : "autocomplete=true";
      const response = await fetch(
        `/product/indexajax?q=${encodeURIComponent(query)}&${templateParam}`,
        {
          signal: this.currentRequest.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const payload = data?.data || null;

      if (payload?.html) {
        this.displayResults(payload.html);
      } else {
        this.displayNoResults();
      }
    } catch (error) {
      if (error.name === "AbortError") {
        // Request was cancelled, do nothing
        return;
      }
      console.error("Search error:", error);
      this.displayError();
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }

  displayResults(html) {
    this.resultContainer.innerHTML = html;
    this.showResults();

    // Add click handlers to result items
    const resultItems = this.resultContainer.querySelectorAll(
      ".autocomplete-product-item"
    );
    resultItems.forEach((item) => {
      item.addEventListener("click", () => {
        this.hideResults();
      });
    });

    // Add click handler for "View all results" link
    const viewAllLink = this.resultContainer.querySelector(".view-all-link");
    if (viewAllLink) {
      viewAllLink.addEventListener("click", (e) => {
        e.preventDefault();
        const keyword = viewAllLink.dataset.searchKeyword;
        if (keyword) {
          // Redirect to clean URL with search keyword only
          const url = new URL("/product", window.location.origin);
          url.searchParams.set("q", keyword.trim());
          window.location.href = url.toString();
        }
        this.hideResults();
      });
    }

    // Reinitialize wishlist buttons for search results
    this.reinitializeWishlistButtons();
  }

  displayNoResults() {
    this.resultContainer.innerHTML = `
            <div class="p-4 text-center text-gray-500">
                <p>Không tìm thấy sản phẩm nào</p>
            </div>
        `;
    this.showResults();
    // No need to reinitialize wishlist for no results
  }

  displayError() {
    this.resultContainer.innerHTML = `
            <div class="p-4 text-center text-red-500">
                <p>Có lỗi xảy ra khi tìm kiếm</p>
            </div>
        `;
    this.showResults();
    // No need to reinitialize wishlist for error state
  }

  showLoading() {
    this.resultContainer.innerHTML = `
            <div class="autocomplete-loading">
                <div class="spinner"></div>
                <p class="ml-2 text-gray-500">Đang tìm kiếm...</p>
            </div>
        `;
    this.showResults();
  }

  hideLoading() {
    // Loading state is handled in displayResults or displayNoResults
  }

  showResults() {
    if (this.searchType === "full") {
      this.resultContainer.classList.remove("hidden");
    } else {
      this.resultContainer.style.display = "block";
    }
  }

  hideResults() {
    if (this.searchType === "full") {
      this.resultContainer.classList.add("hidden");
    } else {
      this.resultContainer.style.display = "none";
    }
  }

  handleKeydown(e) {
    const results = this.resultContainer.querySelectorAll(
      ".autocomplete-product-item"
    );
    const currentIndex = Array.from(results).findIndex((item) =>
      item.classList.contains("selected")
    );

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        this.navigateResults(results, currentIndex, 1);
        break;
      case "ArrowUp":
        e.preventDefault();
        this.navigateResults(results, currentIndex, -1);
        break;
      case "Enter":
        if (currentIndex >= 0 && results[currentIndex]) {
          e.preventDefault();
          results[currentIndex].click();
        }
        break;
      case "Escape":
        this.hideResults();
        this.input.blur();
        break;
    }
  }

  navigateResults(results, currentIndex, direction) {
    if (results.length === 0) return;

    // Remove current selection
    if (currentIndex >= 0) {
      results[currentIndex].classList.remove("selected");
    }

    // Calculate new index
    let newIndex;
    if (currentIndex === -1) {
      newIndex = direction > 0 ? 0 : results.length - 1;
    } else {
      newIndex = currentIndex + direction;
      if (newIndex < 0) newIndex = results.length - 1;
      if (newIndex >= results.length) newIndex = 0;
    }

    // Add selection to new item
    results[newIndex].classList.add("selected");
    results[newIndex].scrollIntoView({ block: "nearest" });
  }

  // Public method to clear search
  clear() {
    this.input.value = "";
    this.hideResults();
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    if (this.currentRequest) {
      this.currentRequest.abort();
    }
  }

  // Reinitialize wishlist buttons for search results
  reinitializeWishlistButtons() {
    // Check if WishlistManager is available
    if (
      window.WishlistManager &&
      typeof window.WishlistManager.reinitializeButtons === "function"
    ) {
      window.WishlistManager.reinitializeButtons().catch((error) => {
        console.error(
          "Error reinitializing wishlist buttons from search results:",
          error
        );
      });
    }
  }

  // Public method to destroy instance
  destroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    if (this.currentRequest) {
      this.currentRequest.abort();
    }
    this.hideResults();
    // Remove event listeners if needed
  }
}

// Export default for compatibility
export default ProductAutocomplete;
